import { sortDates } from "@omnia/shared/utils/utils"
import { FieldViewModel, isNumber, LayerTypeId } from "../../../omnia"
import { IDataLayer, IDataLayerZone, INtrReq, NutrientType } from "../../../types"
import { getNutrientTypes } from "../../../utils"
import { INutritionMapViewContext } from "../types"
import { NutritionMapView } from "./view"
import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { DatalayerZone } from "@omnia/shared/layers/datalayerZone"
import { MapGeometryType } from "@omnia/shared/generated/enums"
import { RequirementsLayerViewerFactory } from "@omnia/shared/nutrition/map/requirementsLayerViewer"
import { RequirementsZone } from "@omnia/shared/nutrition/map/requirementsZone"

export class RequirementsMapView extends NutritionMapView {

    static create(ctxt: INutritionMapViewContext) {

        let views = getNutrientTypes().map(nutrientType => {

            let view = new RequirementsMapView("Initial requirements", "req", nutrientType, ctxt);
            view.setup();
            return view;

        })

        return views;

    }

    private static readonly layerTypeId = LayerTypeId.NutrientRequirements;

    protected constructor(name: string, key: string, protected nutrientType: NutrientType, protected ctxt: INutritionMapViewContext) {
        super(name, key, nutrientType);
    }

    protected setup() {

        this._layerType = this.ctxt.nutritionSvc.getDataLayerType(RequirementsMapView.layerTypeId);

        const viewer = this._layerViewer = this.createViewer();

        this.setupColors(viewer.colors);

    }

    protected createViewer() {

        return RequirementsLayerViewerFactory.createForNutrient(this.nutrientType, "")

    }

    protected createLayers() {

        const nutrientKey = NutrientType[this._nutrient]

        const ctxt = this.ctxt.datalayerContext
        const cropping = this.ctxt.fieldSvc.cropping.filter(c => c.hasBoundary())

        return cropping
            .sort((a, b) => sortDates(a.startDate, b.startDate))
            .map((c, index) => {

                const dl = this.createCroppingDatalayer(index + 1, c, nutrientKey)

                if (dl) {

                    const vm = new LayerViewModel(ctxt, this.ctxt.map, dl, c.fieldVm)

                    // // TODO: setup spécifique pour affichage d'un "layer label"
                    // vm.setLayerLabel(c.fieldName)

                    return vm
                }


            }).filter(l => !!l)

    }

    protected createCroppingDatalayer(layerId: number, cropping: FieldViewModel, nutrientKey: string): IDataLayer {

        let requirements = this.ctxt.planReader.getInitialRequirements(cropping.croppingId) || [];

        requirements = this.filterNutrientGroup(requirements);

        if (requirements.length) {
            return this.createVirtualLayer(layerId, cropping, requirements, nutrientKey);

        }
    }

    protected createVirtualLayer(layerId: number, cropping: FieldViewModel, requirements: INtrReq[], nutrientKey: string): IDataLayer {

        const zones: IDataLayerZone[] = requirements
            .map(r => {

                const value = r["Required" + nutrientKey] || 0;

                const name = r.Zone ? r.ZoneNumber.toString() : cropping.fieldName;
                const geom = r.Geometry || cropping.cropping.Boundary;
                const area = r.Geometry ? r.Area : (cropping.area * 10000)

                let zone = <IDataLayerZone>{
                    DataLayerZoneId: r.NtrReqId,
                    Number: r.ZoneNumber,
                    Geometry: geom,
                    Area: area,
                    Value: value,
                    Name: name,
                    Data: r
                }

                return zone;

            });

        return this.createLayer(layerId, cropping, zones)

    }

    protected createLayer(layerId: number, cropping: FieldViewModel, zones: IDataLayerZone[]) {

        let layer: IDataLayer = {
            DataLayerId: layerId,
            GeometryType: MapGeometryType.Polygon,
            TypeId: this._layerType.TypeId,
            Type: this._layerType,
            FieldId: cropping.fieldId,
            CroppingId: cropping.croppingId,
            Zones: zones
        }

        return layer;

    }

    protected applyZoneOverride(zone: DatalayerZone, ovr: any, refresh: boolean) {

        if (zone instanceof RequirementsZone) {

            let zoneInstance = zone;
            let zoneData = zone.zone;
            let req = <INtrReq>zoneData.Data;


            let key = "Required" + this._nutrientKey;

            let value = ovr ? ovr[key] : undefined;
            let initValue = req[key];

            zoneInstance.value = isNumber(value) ? value : initValue;

            if (refresh) {
                zoneInstance.refresh();
                this.ctxt.map.refreshSelectInteraction();
            }

        }

    }

}
